import { db } from "firebase.js"; // Firebase Firestore
import {
  collection,
  addDoc,
  query,
  where,
  getDocs,
  updateDoc,
  doc,
  getDoc,
  serverTimestamp,
} from "firebase/firestore";
import { canManage } from "./tournamentService";
import * as Sentry from "@sentry/react";

//  Register a user in Firestore (only if the limit isn't reached)
export const handleRegister = async (
  tournament,
  registrations,
  user,
  teamName,
  teamMateId = null,
  teamMateName = null
) => {
  if (!user) return alert("Vous devez être connecté pour vous inscrire !");

  const limit = tournament.registrationLimit || 60;
  const registrationCount = registrations?.length || 0;

  if (registrationCount >= limit) {
    alert("Le nombre maximum d'inscriptions a été atteint !");
    return;
  }

  const isAlreadyRegistered = registrations?.some(
    (reg) => reg.userId === user.uid || reg.teamMateId === user.uid
  );
  if (isAlreadyRegistered) {
    alert("L'inscription a échoué car vous êtes déjà inscrit à ce tournoi.");
    return;
  }

  // ✅ Check if teammate is already registered or already picked
  if (teamMateId) {
    const isTeammateUnavailable = registrations?.some(
      (reg) => reg.userId === teamMateId || reg.teamMateId === teamMateId
    );

    if (isTeammateUnavailable) {
      alert("Ce partenaire est déjà inscrit ou choisi par un autre joueur.");
      return;
    }
  }

  try {
    // ✅ Register the current player
    await addDoc(collection(db, "registrations"), {
      tournamentId: tournament.id,
      userId: user.uid,
      name: user.name || "Anonyme",
      teamName: teamName || user.name,
      teamMateId: teamMateId || null,
      teamMateName: teamMateName || null,
      discarded: false,
      createdAt: serverTimestamp(),
    });
  } catch (error) {
    Sentry.captureException(error);
    console.error("Erreur lors de l'inscription:", error.message);
    alert("Une erreur est survenue lors de l'inscription.");
  }
};

//  User removes their own registration (soft delete)
export const handleRemoveRegistration = async (registrationId) => {
  try {
    const regRef = doc(db, "registrations", registrationId);

    // ✅ Fetch the registration to get userId and teamMateId
    const regSnap = await getDoc(regRef);
    if (!regSnap.exists()) throw new Error("Inscription introuvable.");

    // ✅ Soft-delete the registration
    await updateDoc(regRef, { discarded: true });
  } catch (error) {
    console.error(
      "Erreur lors de la suppression de l'inscription:",
      error.message
    );
  }
};

export const handleEditTeamName = async (registrationId, newTeamName) => {
  try {
    const regRef = doc(db, "registrations", registrationId);
    await updateDoc(regRef, { teamName: newTeamName });
  } catch (error) {
    Sentry.captureException(error);
    console.error("Erreur lors de la modification de l'équipe:", error.message);
  }
};

//  Admin discards all registrations for a tournament
export const handleDiscardAllRegistrations = async (tournamentId, user) => {
  if (!canManage(user, tournamentId)) return alert("Accès refusé.");

  try {
    const q = query(
      collection(db, "registrations"),
      where("tournamentId", "==", tournamentId)
    );
    const querySnapshot = await getDocs(q);

    querySnapshot.forEach(async (registration) => {
      await updateDoc(doc(db, "registrations", registration.id), {
        discarded: true,
      });
    });
  } catch (error) {
    Sentry.captureException(error);
    console.error(
      "Erreur lors de l'annulation des inscriptions:",
      error.message
    );
  }
};

export const getRegistrationsByTournament = async (tournamentId) => {
  const q = query(
    collection(db, "registrations"),
    where("tournamentId", "==", tournamentId),
    where("discarded", "==", false)
  );
  const snapshot = await getDocs(q);
  console.log(`Firestore read count: ${snapshot.docs.length}`);
  return snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
};
